import { AspectRatio, Box, Text, Wrap } from "@chakra-ui/react";
import PampaImage from "@raiden/library/components/PampaImage";
import useLocale from "@raiden/library/hooks/useLocale";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import NextLink from "next/link";
import { useIntl } from "react-intl";
import { getPostSlug } from "../../../helpers/posts/getPostSlug";

/**
 * @typedef {Object} Props
 * @property {import("@raiden/library/types/Post").Post} post
 *
 * @param {Props} props
 */
export function ArticleCard({ post }) {
  const intl = useIntl();

  const translate = useTranslate();

  const { locale, defaultLocale } = useLocale();

  return (
    <NextLink
      href={generateFrontPath({
        id: "@front.articles.view",
        parameters: {
          articleSlug: getPostSlug({ post, locale, defaultLocale }),
        },
      })}
      passHref>
      <Box
        as="a"
        display="block"
        transition="all .25s ease"
        role="group"
        p="1rem">
        <Box boxShadow="0 0 0 1px #eee">
          <AspectRatio ratio={16 / 9}>
            <PampaImage
              transition="all .25s ease"
              _groupHover={{
                transform: "scale(1.05)",
              }}
              image={post.current_revision?.cover}
              mode="crop"
              width="full"
              format="default"
              placeholderSrc={generateFrontPath({
                id: "@front.internal-assets",
                parameters: {
                  filePath: "placeholder-article.svg",
                },
                includeBasePath: true,
              })}
              alt={
                translate(post.current_revision?.title) ??
                intl.formatMessage({ defaultMessage: "article" })
              }
            />
          </AspectRatio>
        </Box>

        <Box
          px="0"
          transition="all .25s ease"
          pb=".5rem"
          pr="15px"
          borderColor="brandPrimary.300"
          _groupHover={{ borderLeftWidth: "3px", pl: "12px", pr: "0" }}>
          <Text mt="16px" noOfLines={1}>
            {translate(post.current_revision?.title)}
          </Text>

          <Text
            variant="text-medium"
            color="brandPrimary.400"
            noOfLines={1}
            mt="0.25rem">
            {translate(post.current_revision?.subtitle)}
          </Text>

          <Wrap mt="0.75rem">
            {post.categories?.map((category) => (
              <Text
                variant="text-small"
                backgroundColor="brandPrimary.300"
                color="#fff"
                px="0.5rem"
                key={category.id}>
                {translate(category.name)}
              </Text>
            ))}

            {post.publication_date !== null && (
              <Text variant="text-small">
                {intl.formatDate(post.publication_date, {
                  day: "numeric",
                  month: "numeric",
                  year: "numeric",
                })}
              </Text>
            )}
          </Wrap>
        </Box>
      </Box>
    </NextLink>
  );
}
